import React, { useEffect, useRef, useState } from 'react';
// import { useLocation, useNavigate } from 'react-router-dom'; // Uncomment if you need navigation

// Firebase - Assuming you have these configured in '../firebase.js'
// You'll need to create this file or adjust the import path.
// Example firebase.js:
// import { initializeApp } from 'firebase/app';
// import { getFirestore } from 'firebase/firestore';
// import { getStorage } from 'firebase/storage';
// import { getAuth } from 'firebase/auth';
// const firebaseConfig = { /* YOUR FIREBASE CONFIG */ };
// const app = initializeApp(firebaseConfig);
// export const db = getFirestore(app);
// export const Storage = getStorage(app); // Note: Storage not storage
// export const auth = getAuth(app);
import { db, Storage, auth } from '../firebase'; // Adjust path as needed
import { ref as storageRef, uploadBytes, getDownloadURL, uploadString } from 'firebase/storage';
import { setDoc, doc, collection, serverTimestamp, addDoc } from 'firebase/firestore';

// UI
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DPI = 96;

const HandTagsEditor = () => {
  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  // useNavigate hook if redirection is needed after save
  // const navigate = useNavigate();

  // UI State
  const [loading, setLoading] = useState(false); // General loading for file processing
  const [isSaving, setIsSaving] = useState(false); // Specific for product save operation
  const [librariesLoaded, setLibrariesLoaded] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  // Content State
  const [currentFrontFile, setCurrentFrontFile] = useState(null);
  const [currentBackFile, setCurrentBackFile] = useState(null);
  const [frontImageUrl, setFrontImageUrl] = useState(null); // Data URL from PDF/Image
  const [backImageUrl, setBackImageUrl] = useState(null);  // Data URL from PDF/Image
  
  // Storing actual design dimensions in inches
  const [frontActualDimensions, setFrontActualDimensions] = useState({ width: 0, height: 0 });
  const [backActualDimensions, setBackActualDimensions] = useState({ width: 0, height: 0 });
  
  const [modifications, setModifications] = useState([]);
  const [currentImageSize, setCurrentImageSize] = useState({ width: 0, height: 0 }); // For current canvas display

  // Product Information
  const [productName, setProductName] = useState('');
  const productNameRef = useRef(''); // To hold product name at time of save for consistent file naming

  // Hand tag settings
  const [currentSide, setCurrentSide] = useState('front');
  
  // Pricing state
  const [baseCost, setBaseCost] = useState(0);
  const [printCost, setPrintCost] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);

  const handTagImages = {
    front: '/tag-front.jpg', 
    back: '/tag-back.jpg',   
  };

  const tagConfig = {
    imageWidth: 220 * 1.55,
    imageHeight: 270 * 1.55,
    printableAreas: {
      front: { width: 2 * DPI, height: 3.5 * DPI, x: (475 - 3.5 * DPI) / 2, y: (308 - 2 * DPI) / 2 },
      back: { width: 2 * DPI, height: 3.5 * DPI, x: (495 - 3.5 * DPI) / 2, y: (310 - 2 * DPI) / 2 }
    },
    label: '3.5" x 2"',
    basePrice: 5,
    printPrice: 0
  };

  const getPrintableArea = (sideOverride = currentSide) => tagConfig.printableAreas[sideOverride];
  const calculateBaseCost = () => tagConfig.basePrice;
  const calculatePrintCost = (hasFront, hasBack) => (hasFront ? tagConfig.printPrice : 0) + (hasBack ? tagConfig.printPrice : 0);
  const calculateTotalPrice = (base, print) => base + print;

  useEffect(() => {
    const newBaseCost = calculateBaseCost();
    const newPrintCost = calculatePrintCost(!!frontImageUrl, !!backImageUrl);
    const newTotalPrice = calculateTotalPrice(newBaseCost, newPrintCost);
    setBaseCost(newBaseCost); setPrintCost(newPrintCost); setTotalPrice(newTotalPrice);
  }, [frontImageUrl, backImageUrl]);

  useEffect(() => {
    productNameRef.current = productName;
  }, [productName]);

  // Load external libraries (pdf.js, fabric.js)
  useEffect(() => {
    const loadScript = (src) => new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src; script.async = true; script.onload = resolve; script.onerror = reject;
      document.head.appendChild(script);
    });

    const loadLibraries = async () => {
      try {
        if (!window.pdfjsLib) {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js');
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }
        if (!window.fabric) {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.0/fabric.min.js');
        }
        setLibrariesLoaded(true);
      } catch (error) {
        console.error('Error loading libraries:', error);
        toast.error('Failed to load required libraries. Please refresh.');
      }
    };
    loadLibraries();
  }, []);
  
  // Update image size info (for UI and for saving actual dimensions)
  const updateImageSizeInfo = (canvasInstance = fabricCanvasRef.current, activeSideForDimensionUpdate = currentSide) => {
    if (!canvasInstance) return;
    const contentImage = canvasInstance.getObjects().find(obj => obj.isContent);
    
    let newSize = { width: 0, height: 0 };
    if (contentImage) {
      const scaledWidthPx = contentImage.getScaledWidth();
      const scaledHeightPx = contentImage.getScaledHeight();
      const widthInches = (scaledWidthPx / DPI).toFixed(2);
      const heightInches = (scaledHeightPx / DPI).toFixed(2);
      newSize = { width: parseFloat(widthInches), height: parseFloat(heightInches) };
    }
    
    setCurrentImageSize(newSize); // For UI display for the active canvas

    if (activeSideForDimensionUpdate === 'front') {
        setFrontActualDimensions(newSize);
    } else if (activeSideForDimensionUpdate === 'back') {
        setBackActualDimensions(newSize);
    }
  };

  // Initialize Fabric.js canvas and event listeners
  useEffect(() => {
    if (!librariesLoaded || !canvasRef.current || fabricCanvasRef.current) return;

    const canvas = new window.fabric.Canvas(canvasRef.current, {
        backgroundColor: 'transparent',
        width: tagConfig.imageWidth,
        height: tagConfig.imageHeight,
    });
    fabricCanvasRef.current = canvas;
    
    const handleContentModification = (e) => {
        const obj = e.target;
        if (!obj || !obj.isContent) return; 
        const modification = {
            id: obj.id || Date.now(), type: obj.type, left: obj.left, top: obj.top,
            scaleX: obj.scaleX, scaleY: obj.scaleY, angle: obj.angle, side: currentSide
        };
        setModifications(prev => [...prev.filter(m => m.id !== modification.id), modification]);
        setTimeout(() => updateImageSizeInfo(canvas, currentSide), 100); 
    };

    canvas.on('object:modified', handleContentModification);
    canvas.on('object:scaling', (e) => { if (e.target && e.target.isContent) updateImageSizeInfo(canvas, currentSide); });
    canvas.on('object:added', (e) => { if (e.target && e.target.isContent) updateImageSizeInfo(canvas, currentSide); });
    canvas.on('object:removed', () => updateImageSizeInfo(canvas, currentSide)); // Update on removal too
    
    switchSide('front').catch(console.error); // Initial load

    return () => {
      fabricCanvasRef.current?.dispose();
      fabricCanvasRef.current = null;
    };
  }, [librariesLoaded]); // currentSide dependency removed from here, switchSide is called explicitly

  // Update tag background and load content image if available for the side
  const updateTagBackground = (canvas, side) => new Promise((resolveUpdateTagBg) => {
    const existingBg = canvas.getObjects().find(obj => obj.isTagBackground);
    if (existingBg) canvas.remove(existingBg);
    const existingClipRect = canvas.getObjects().find(obj => obj.isClipRect);
    if (existingClipRect) canvas.remove(existingClipRect);

    const imageUrl = handTagImages[side];
    window.fabric.Image.fromURL(imageUrl, async (img) => {
      if (!img) {
        console.error('Failed to load tag image:', imageUrl);
        resolveUpdateTagBg(); // Resolve even if background fails, to not block further ops
        return;
      }
      img.set({
        isTagBackground: true, selectable: false, evented: false, hoverCursor: 'default',
        left: 0, top: 0, scaleX: canvas.width / img.width, scaleY: canvas.height / img.height
      });
      canvas.add(img).sendToBack(img);

      const { x, y, width, height } = getPrintableArea(side);
      const clipRect = new window.fabric.Rect({
        left: x, top: y, width, height, fill: '#ffffff00', // Transparent fill
        stroke: '#0066cc', strokeWidth: 1, strokeDashArray: [5, 5],
        isClipRect: true, selectable: false, evented: false, excludeFromExport: true,
      });
      canvas.add(clipRect);

      const contentUrl = side === 'front' ? frontImageUrl : backImageUrl;
      if (contentUrl) {
          try {
              await loadImageToCanvas(contentUrl, canvas, side);
          } catch (error) {
              console.error(`Error loading content image for ${side} side in updateTagBackground:`, error);
              toast.error(`Failed to load design for ${side} side.`);
          }
      } else {
          canvas.renderAll();
          updateImageSizeInfo(canvas, side); // Reset/update sizes if no content
      }
      resolveUpdateTagBg();
    }, { crossOrigin: 'anonymous' });
  });

  // Switch between Front and Back views on the canvas
  const switchSide = async (side) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return Promise.reject(new Error("Canvas not initialized for switchSide"));
      
      // Avoid unnecessary re-renders if already on the correct side with background
      if (currentSide === side && canvas.getObjects().some(o => o.isTagBackground && o.src?.includes(handTagImages[side]))) {
          // If content needs refresh (e.g. frontImageUrl changed but side is already front)
          const contentUrl = side === 'front' ? frontImageUrl : backImageUrl;
          const currentContent = canvas.getObjects().find(o => o.isContent);
          if (contentUrl && (!currentContent || currentContent.src !== contentUrl)) {
              // Content changed, need to reload it
          } else if (!contentUrl && currentContent) {
              // Content removed, need to clear it
          } else {
              return Promise.resolve(); // Already correct and content matches
          }
      }

      setCurrentSide(side); // Update currentSide state first
      setFileInputKey(Date.now()); 
  
      canvas.getObjects().forEach(obj => { if (obj.isContent) canvas.remove(obj); });
      // updateTagBackground handles loading new background and content image if present
      await updateTagBackground(canvas, side); 
  };
  
  // Load user's design (PDF/Image) onto the canvas for the specified side
  const loadImageToCanvas = (imageUrl, canvas, side) => new Promise((resolve, reject) => {
    canvas.getObjects().forEach(obj => { if (obj.isContent) canvas.remove(obj); });
    
    window.fabric.Image.fromURL(imageUrl, (img) => {
      if (!img) {
        console.error(`Fabric.Image.fromURL failed for ${side}`);
        reject(new Error(`Failed to load image from URL for ${side} side.`));
        return;
      }
      img.set({ isContent: true, selectable: true, id: `content_${side}` });

      const { x, y, width, height } = getPrintableArea(side);
      img.clipPath = new window.fabric.Rect({
          left: x, top: y, width, height, absolutePositioned: true
      });

      canvas.add(img);
      repositionContent(img, canvas, side);
      canvas.setActiveObject(img).renderAll();

      setTimeout(() => { // Ensure rendering and scaling are applied
        updateImageSizeInfo(canvas, side); // Crucial: update dimensions for this specific side
        resolve();
      }, 100);
    }, { crossOrigin: 'anonymous' });
  });

  // Helper to center and scale content within printable area
  const repositionContent = (contentImage, canvas, side) => {
    if (!contentImage) return;
    const { x, y, width, height } = getPrintableArea(side);
    const padding = 10; 
    const scale = Math.min((width - padding) / contentImage.width, (height - padding) / contentImage.height, 1);
    contentImage.scale(scale);
    contentImage.set({
      left: x + (width - contentImage.getScaledWidth()) / 2,
      top: y + (height - contentImage.getScaledHeight()) / 2,
    }).setCoords();
  };

  // Handle file input: convert PDF to image, or read image file
  const handleFileChange = async (e, side) => {
    if (!librariesLoaded) { toast.warn("Libraries not loaded yet."); return; }
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    toast.info(`Processing ${file.name}...`, { autoClose: false, toastId: "fileProcessing" });

    if (side === 'front') setCurrentFrontFile(file);
    else setCurrentBackFile(file);

    try {
      const imgURL = file.type === 'application/pdf' ? await renderPDFToImageURL(file) : await readFileToDataURL(file);
      
      if (side === 'front') setFrontImageUrl(imgURL);
      else setBackImageUrl(imgURL);

      // If the current canvas side matches the uploaded file's side, load it immediately
      if (currentSide === side) {
        await loadImageToCanvas(imgURL, fabricCanvasRef.current, side);
      }
      toast.success(`${file.name} processed successfully!`);
    } catch (err) {
      console.error('Error processing file:', err);
      toast.error('Failed to load file. Please try a different file or check console.');
    } finally {
      setLoading(false);
      toast.dismiss("fileProcessing");
      setFileInputKey(Date.now()); // Reset file input
    }
  };

  // Convert PDF page to Data URL
  const renderPDFToImageURL = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1); // Using first page
    const viewport = page.getViewport({ scale: 2.0 }); // Scale for better resolution
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = viewport.width; tempCanvas.height = viewport.height;
    const ctx = tempCanvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;
    return tempCanvas.toDataURL('image/png');
  };

  // Read image file to Data URL
  const readFileToDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject; reader.readAsDataURL(file);
  });
  
  // Delete selected object from canvas
  const deleteSelected = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const activeObjects = canvas.getActiveObjects();
    if (activeObjects.length > 0) {
        activeObjects.forEach(obj => { 
            if (obj.isContent) canvas.remove(obj); // Triggers 'object:removed' listener
        });
        canvas.discardActiveObject().renderAll();
        // updateImageSizeInfo is called by 'object:removed' listener
    } else {
        toast.info("No object selected to delete.");
    }
  };

  // Clear design for a specific side
  const clearSide = (sideToClear) => {
    if (sideToClear === 'front') {
      setFrontImageUrl(null); setCurrentFrontFile(null);
      setFrontActualDimensions({ width: 0, height: 0 });
    } else {
      setBackImageUrl(null); setCurrentBackFile(null);
      setBackActualDimensions({ width: 0, height: 0 });
    }

    if (sideToClear === currentSide) {
      const canvas = fabricCanvasRef.current;
      if(canvas) {
        canvas.getObjects().forEach(obj => { if (obj.isContent) canvas.remove(obj); });
        canvas.renderAll();
        updateImageSizeInfo(canvas, sideToClear); // Explicitly update UI and stored dimensions
      }
    }
    setModifications(prev => prev.filter(m => m.side !== sideToClear));
    toast.info(`${sideToClear.charAt(0).toUpperCase() + sideToClear.slice(1)} side design cleared.`);
  };

  // Responsive canvas scaling
  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container || !librariesLoaded || !fabricCanvasRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      const canvas = fabricCanvasRef.current;
      if (!canvas || !canvas.wrapperEl) return;
      const canvasOriginalWidth = canvas.getWidth();
      if (canvasOriginalWidth === 0) return;

      const maxWidth = container.clientWidth - 20; 
      const scale = Math.min(maxWidth / canvasOriginalWidth, 1);
      
      canvas.wrapperEl.style.transform = `scale(${scale})`;
      canvas.wrapperEl.style.transformOrigin = 'top left';
      container.style.height = `${canvas.getHeight() * scale + 20}px`; 
    });

    resizeObserver.observe(container);
    // Initial resize call
    const timer = setTimeout(() => {
        if (fabricCanvasRef.current && fabricCanvasRef.current.getWidth() > 0) {
            const maxWidth = container.clientWidth - 20;
            const scale = Math.min(maxWidth / fabricCanvasRef.current.getWidth(), 1);
            if (fabricCanvasRef.current.wrapperEl) {
                 fabricCanvasRef.current.wrapperEl.style.transform = `scale(${scale})`;
                 fabricCanvasRef.current.wrapperEl.style.transformOrigin = 'top left';
                 container.style.height = `${fabricCanvasRef.current.getHeight() * scale + 20}px`;
            }
        }
    }, 250); // Increased delay slightly

    return () => { resizeObserver.disconnect(); clearTimeout(timer); }
  }, [librariesLoaded, currentSide]); // Re-run if currentSide changes, as canvas content might resize parent
  
  // --- Start Firebase Save Logic ---

  // Helper: Convert Data URL to Blob
  function dataURLtoBlob(dataurl) {
    var arr = dataurl.split(','), mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) throw new Error("Invalid Data URL: Mime type not found.");
    var mime = mimeMatch[1],
        bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    while(n--){
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], {type:mime});
  }

  // Upload original PDF (if any) to Firebase Storage
  const uploadPdfToStorage = async (file, designId, side, userId) => {
    if (!file) return null;
    const sanProductName = productNameRef.current.replace(/\s+/g, '_') || 'untitled';
    // Path: BrandingImages/HandTags/{userId}/{designId}_{side}.pdf
    const filePath = `BrandingImages/HandTags/${designId}_${side}.pdf`;
    const fileRef = storageRef(Storage, filePath);
    
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };

  // Generate mockup PNG data URL from current canvas state
  const generateMockupDataURL = (canvasInstance) => {
    if (!canvasInstance) return null;
    const clipRect = canvasInstance.getObjects().find(obj => obj.isClipRect);
    const originalStroke = clipRect ? clipRect.stroke : null;
    const originalFill = clipRect ? clipRect.fill : null;

    if (clipRect) clipRect.set({ stroke: 'transparent', fill: 'transparent' }); // Hide dashed lines for export
    
    canvasInstance.renderAll();
    const dataURL = canvasInstance.toDataURL({ format: 'png', quality: 0.9, multiplier: 1.5 }); // Good quality for mockups
    
    if (clipRect) { // Restore
      if (originalStroke) clipRect.set({ stroke: originalStroke });
      if (originalFill) clipRect.set({ fill: originalFill }); // Restore original fill if any
      else clipRect.set({ fill: '#ffffff00'}); // default transparent fill
      canvasInstance.renderAll();
    }
    return dataURL;
  };
  
  // Save current canvas view as mockup to Firebase Storage
  const saveCanvasMockupToStorage = async (canvasInstance, designId, side, userId) => {
    if (!canvasInstance) throw new Error("Canvas not available for mockup generation.");
    const dataURL = generateMockupDataURL(canvasInstance);
    if (!dataURL) throw new Error(`Failed to generate ${side} mockup image data.`);

    const sanProductName = productNameRef.current.replace(/\s+/g, '_') || 'untitled';
    // Path: BrandingMockups/HandTags/{userId}/{designId}_{side}.png
    const filePath = `BrandingMockups/HandTags/${designId}_${side}.png`;
    const fileRef = storageRef(Storage, filePath);

    // uploadString can take data URL directly (if 'data_url' format is specified)
    await uploadString(fileRef, dataURL, 'data_url');
    return await getDownloadURL(fileRef);
  };

  // Main function to save the product design
  const handleSaveProduct = async () => {
    if (!auth.currentUser) {
      toast.error("You must be logged in to save a design.");
      return;
    }
    if (!productName.trim()) {
      toast.error("Please enter a Product Name.");
      return;
    }
    if (!frontImageUrl && !backImageUrl) {
      toast.error("Please upload a design for at least one side.");
      return;
    }

    setIsSaving(true);
    const savingToastId = toast.loading("Saving your amazing Hand Tag design...");

    const userId = auth.currentUser.uid;
    const originalCanvasSide = currentSide; // Remember the side user was on

    // Create a new document in Firestore to get an auto-generated ID
    const designDocRef = doc(collection(db, "users", userId, "BrandingDesigns"));
    const designId = designDocRef.id;

    let frontPdfUrl = null;
    let backPdfUrl = null;
    let frontMockupUrl = null;
    let backMockupUrl = null;

    try {
      // 1. Upload Original PDFs (if they exist)
      if (currentFrontFile) {
        toast.update(savingToastId, { render: "Uploading front PDF..." });
        frontPdfUrl = await uploadPdfToStorage(currentFrontFile, designId, 'front', userId);
      }
      if (currentBackFile) {
        toast.update(savingToastId, { render: "Uploading back PDF..." });
        backPdfUrl = await uploadPdfToStorage(currentBackFile, designId, 'back', userId);
      }

      // 2. Generate and Upload Mockups
      if (frontImageUrl) {
        toast.update(savingToastId, { render: "Preparing front mockup..." });
        if (currentSide !== 'front') await switchSide('front'); // Ensure canvas shows front
        frontMockupUrl = await saveCanvasMockupToStorage(fabricCanvasRef.current, designId, 'front', userId);
      }
      if (backImageUrl) {
        toast.update(savingToastId, { render: "Preparing back mockup..." });
        if (currentSide !== 'back') await switchSide('back'); // Ensure canvas shows back
        backMockupUrl = await saveCanvasMockupToStorage(fabricCanvasRef.current, designId, 'back', userId);
      }

      // Restore original canvas side if it was changed
      if (currentSide !== originalCanvasSide) {
        toast.update(savingToastId, { render: "Finalizing..." });
        await switchSide(originalCanvasSide);
      }

      // 3. Prepare Firestore Payload
const payload = {
  designName: productName.trim(),
  totalPrice: totalPrice,

  frontDesign: frontPdfUrl,
  backDesign: backPdfUrl, 

  size: tagConfig.label, 
  color: null, 

  images: {
    front: frontMockupUrl,
    back: backMockupUrl,
  },

  costs: {
    front: frontImageUrl ? tagConfig.printPrice : 0,
    back: backImageUrl ? tagConfig.printPrice : 0,
  },

  Printsizes: {
    front: frontImageUrl ? frontActualDimensions : null,
    back: backImageUrl ? backActualDimensions : null,
  },

  productName: "HandTag", 
  SKU: `HT-${designId}`,
  baseCost: baseCost,
  PrintCost: printCost, 
  timestamp: serverTimestamp(),
  userId: userId,

  // Optional advanced metadata (commented out if unused)
  // fabricCanvasState: fabricCanvasRef.current ? fabricCanvasRef.current.toJSON([...]) : null,
  // designModifications: modifications,
};


      console.log("Prepared Firestore payload:", payload);
      // 4. Save to Firestore
      toast.update(savingToastId, { render: "Saving design details to database..." });
      await setDoc(designDocRef, payload);

      toast.update(savingToastId, { render: "Design saved successfully!", type: "success", isLoading: false, autoClose: 5000 });
      // navigate('/dashboard/designs'); // Optional: redirect after save

    } catch (error) {
      console.error("Error saving product:", error);
      toast.update(savingToastId, { render: `Error saving design: ${error.message}`, type: "error", isLoading: false, autoClose: 7000 });
    } finally {
      setIsSaving(false);
    }
  };

  // --- End Firebase Save Logic ---


  // Simple Control Card component
  const ControlCard = ({ title, children }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <ToastContainer position="top-right" theme="colored" />
      <header className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm z-10">
          <div className="max-w-screen-2xl mx-auto px-6 py-1">
            <h1 className="text-2xl font-bold text-gray-900">Hand Tags Customizer</h1>
          </div>
      </header>

      <main className="flex-grow grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 overflow-hidden">
        
        <aside className="col-span-12 lg:col-span-3 flex flex-col gap-6 overflow-y-auto pb-4">
          <ControlCard title={`1. Upload for ${currentSide.charAt(0).toUpperCase() + currentSide.slice(1)} Side`}>
              <input
                key={fileInputKey} type="file" accept=".pdf,.png,.jpg,.jpeg" // Accept images too
                onChange={(e) => handleFileChange(e, currentSide)}
                disabled={!librariesLoaded || loading || isSaving}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
              />
              {!librariesLoaded && <div className="mt-2 p-2 text-xs bg-yellow-100 text-yellow-800 rounded">Loading libraries...</div>}
              {loading && <div className="mt-2 p-2 text-xs bg-blue-100 text-blue-800 rounded">Processing file...</div>}
          </ControlCard>

          <div title="2. Product Details">
            <div className="space-y-4">
              <div>
                <label htmlFor="productNameInput" className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                <input
                  id="productNameInput" type="text" value={productName} onChange={(e) => setProductName(e.target.value)}
                  placeholder="e.g., My Awesome Hand Tag"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  disabled={isSaving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tag Size</label>
                <input type="text" value={tagConfig.label} readOnly className="w-full p-2 border border-gray-300 rounded-md bg-gray-100" />
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Side to Edit</label>
              <div className="flex space-x-2">
                <button 
                  onClick={() => switchSide('front').catch(err => toast.error(err.message))} 
                  disabled={isSaving}
                  className={`flex-1 py-2 text-sm rounded-md border-2 font-semibold ${currentSide === 'front' ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-300 bg-gray-200 text-gray-700'} hover:border-blue-400 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Front Side
                </button>
                <button 
                  onClick={() => switchSide('back').catch(err => toast.error(err.message))} 
                  disabled={isSaving}
                  className={`flex-1 py-2 text-sm rounded-md border-2 font-semibold ${currentSide === 'back' ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-300 bg-gray-200 text-gray-700'} hover:border-blue-400 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Back Side
                </button>
              </div>
            </div>
          </div>

          <ControlCard title={`Current Design Size (${currentSide})`}>
            {currentImageSize.width > 0 && currentImageSize.height > 0 ? (
              <div className="text-sm text-gray-600">
                <span className="font-medium text-gray-800">{currentImageSize.width}" W × {currentImageSize.height}" H</span>
                <p className="text-xs text-gray-500 mt-1">Within printable area of {getPrintableArea().width/DPI}" × {getPrintableArea().height/DPI}".</p>
              </div>
            ) : ( <div className="text-sm text-gray-500 text-center py-2">No design loaded or image has zero dimensions.</div> )}
          </ControlCard>
        </aside>

        <section className="col-span-12 lg:col-span-5 flex flex-col bg-white rounded-lg shadow-inner border border-gray-200 overflow-hidden">
          <div ref={canvasContainerRef} className="flex-grow flex items-top pt-10 justify-center p-4 bg-gray-100 overflow-auto relative">
             {isSaving && <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-20"><p className="text-blue-600 font-semibold">Processing for save...</p></div>}
            <div className="relative w-min h-min">
              <canvas ref={canvasRef} />
            </div>
          </div>
        </section>

        <aside className="col-span-12 lg:col-span-4 flex flex-col gap-6 overflow-y-auto pb-4">
          <ControlCard title="Design Status & Pricing">
              <div className="space-y-3">
                {[ { label: 'Front Side', hasDesign: !!frontImageUrl, dimensions: frontActualDimensions },
                   { label: 'Back Side', hasDesign: !!backImageUrl, dimensions: backActualDimensions } ].map(item => (
                  <div key={item.label} className="flex justify-between items-start text-sm">
                    <div>
                      <span className="text-gray-600">{item.label}:</span>
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${item.hasDesign ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                        {item.hasDesign ? 'Uploaded' : 'No Design'}
                      </span>
                      {item.hasDesign && item.dimensions.width > 0 && (
                        <p className="text-xs text-gray-500 mt-0.5">Size: {item.dimensions.width}" × {item.dimensions.height}"</p>
                      )}
                    </div>
                  </div>
                ))}
                <hr/>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Base Cost:</span><span className="font-medium text-gray-800">₹{baseCost.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Print Cost:</span><span className="font-medium text-gray-800">₹{printCost.toFixed(2)}</span></div>
                <hr className="my-2 border-dashed" />
                <div className="flex justify-between items-center text-lg font-bold text-blue-800"><span>Total Price:</span><span>₹{totalPrice.toFixed(2)}</span></div>
              </div>
          </ControlCard>
          
          <ControlCard title="Editing Tools">
            <div className="space-y-2">
                <button 
                  onClick={deleteSelected} 
                  className="w-full px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 text-sm font-medium" 
                  disabled={!librariesLoaded || !(currentSide === 'front' ? frontImageUrl : backImageUrl) || isSaving}
                >
                  Delete Selected from Canvas
                </button>
                <button 
                  onClick={() => clearSide(currentSide)} 
                  className="w-full px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 text-sm font-medium" 
                  disabled={!librariesLoaded || !(currentSide === 'front' ? frontImageUrl : backImageUrl) || isSaving}
                >
                  Clear {currentSide.charAt(0).toUpperCase() + currentSide.slice(1)} Side Design
                </button>
            </div>
          </ControlCard>

          {/* SAVE PRODUCT BUTTON */}
          <ControlCard title="Finalize & Save">
             <button
                onClick={handleSaveProduct}
                disabled={!librariesLoaded || isSaving || (!frontImageUrl && !backImageUrl) || !productName.trim()}
                className="w-full px-4 py-3 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-base"
              >
                {isSaving ? 'Saving...' : 'Save Hand Tag Design'}
              </button>
              {auth.currentUser === null && <p className="text-xs text-red-500 mt-2 text-center">Please log in to save.</p>}
          </ControlCard>
          
          {/* Kept export options for local use if needed, can be removed */}
          {/* <ControlCard title="Local Export Options (Optional)"> ... </ControlCard> */}
        </aside>
      </main>
    </div>
  );
};

export default HandTagsEditor;